import { Suspense } from 'react';

import { Toaster } from '@remente/shared-frontend';
import { logger } from '@remente/shared-frontend';
import { DarkModeProvider } from '@remente/shared-frontend';
import * as Sentry from '@sentry/react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';

import { Icon } from './assets/icons/Icon';
import { ErrorFallback } from './components/ErrorFallback';
import { AuthProvider } from './features/auth/context/AuthProvider';
import { AuthPage } from './features/auth/pages/AuthPage';
import { ForgotPassword } from './features/auth/pages/ForgotPassword';
import { ErrorPage } from './features/redemption/pages/ErrorPage';
import { RedeemPage } from './features/redemption/pages/RedeemPage';
import { NotFound } from './pages/NotFound';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      logger.error('Query error:', error);
      Sentry.captureException(error);
    },
  }),
});

// Old success URLs cannot serve as proof that a link was redeemed.
const ReturnToRedemption = () => {
  const { search } = useLocation();
  return <Navigate to={`/redeem${search}`} replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary fallback={<ErrorFallback />}>
        <BrowserRouter>
          <DarkModeProvider>
            <Suspense fallback={<Icon iconName="spinner" />}>
              <Toaster />
              <AuthProvider>
                <div className="bg-(--color-bgSurfaceDefault)">
                  <Routes>
                    <Route path="/" element={<NotFound />} />
                    <Route path="/signup" element={<AuthPage mode="signup" />} />
                    <Route path="/login" element={<AuthPage mode="login" />} />
                    <Route path="/redeem" element={<RedeemPage />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/success" element={<ReturnToRedemption />} />
                    <Route path="/error" element={<ErrorPage />} />
                    <Route path="/not-found" element={<NotFound />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </div>
              </AuthProvider>
            </Suspense>
          </DarkModeProvider>
        </BrowserRouter>
      </ErrorBoundary>

      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
    </QueryClientProvider>
  );
}

export default App;
